import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as payActions } from 'modules/pay/actions'

// Components
import { Button, Spinner, Error } from '@tracklib/kit'
import { PaymentMethod } from 'components/subscribe/subscribeconfirm'
import { SuccessBox, PaymentError, PaymentSuccess } from 'components/pay/paymentstatus'
import { Summary } from 'kit'
import ChangePaymentMethod from 'components/pay/changepaymentmethod'
import CheckoutLayout from 'components/pay/checkoutlayout'
import PayButton from 'components/pay/paybutton'
import SummaryList from 'components/pay/summarylist'

// Hooks
import { useRequest } from 'modules/requests/hooks'
import { useSavedPaymentMethod } from 'modules/pay/hooks'

// Types
import { State, RevenueOrder } from 'types'

// Utils
import { priceString } from 'components/base/price'

import styles from './licensepay.css'

type DerivativePayProps = {
  paymentId: number
}

const DerivativePay = ({ paymentId }: DerivativePayProps): JSX.Element => {
  const dispatch = useDispatch()
  const savedPaymentMethod = useSavedPaymentMethod()
  const revenueOrder = useSelector<State, RevenueOrder>(state => state.entities.revenueOrders[paymentId])
  const fetchRevenueOrderRequest = useRequest('FETCH_REVENUE_ORDER')
  const fetchProfileRequest = useRequest('FETCH_PROFILE')
  const payRevenueOrderRequest = useRequest('PAY_REVENUE_ORDER')
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (!revenueOrder) {
      dispatch(payActions.fetchRevenueOrder(paymentId))
    }
  }, [dispatch, revenueOrder, paymentId])

  // Catch pay errors
  useEffect(() => {
    if (payRevenueOrderRequest.hasError) {
      setHasError(true)
    }
  }, [payRevenueOrderRequest.hasError])

  const { order_items, total_amount, total_amount_already_paid, state, currency, vat_rate, vat_amount } = revenueOrder || {}
  const subItems = [
    { label: 'Subtotal:', value: priceString(total_amount, currency) },
    { label: `${vat_rate}% VAT:`, value: priceString(vat_amount, currency) },
  ]

  const orderItems = Object.keys(order_items || {}).map(period => order_items[period])

  let mainContent = null
  if (fetchRevenueOrderRequest.isLoading) {
    mainContent = <Spinner minHeight={300} delay={0} />
  } else if (fetchRevenueOrderRequest.isDone && !revenueOrder) {
    return <Error message="Not found" />
  } else if (fetchProfileRequest.isDone && !savedPaymentMethod) {
    mainContent = <ChangePaymentMethod alwaysExpanded hideRemoveButton />
  } else if (state === 'PAID') {
    mainContent = (
      <>
        <PaymentSuccess />
        <div className={styles.divider}>
          <SuccessBox
            buttonTo="/yoursongs"
            buttonText="Back to your songs"
            title="What happens now?"
            description="Absolutely nothing. You won't have to worry about revenue reporting until the next half-year."
          />
        </div>
      </>
    )
  } else if (hasError) {
    mainContent = (
      <div className={styles.errorWrapper}>
        <PaymentError button={<Button onClick={() => setHasError(false)}>Try again</Button>} />
      </div>
    )
  } else if (savedPaymentMethod) {
    mainContent = (
      <>
        <PaymentMethod />
        <PayButton
          isLoading={payRevenueOrderRequest.isLoading}
          onClick={() => dispatch(payActions.payRevenueOrder(revenueOrder.id))}
          buttonText="Pay"
        />
      </>
    )
  }

  return (
    <CheckoutLayout
      sidebarContent={
        revenueOrder && (
          <Summary
            title="Summary of royalties"
            listComponent={<SummaryList items={orderItems} currency={currency} />}
            subItems={
              total_amount_already_paid
                ? [
                    {
                      label: 'Distributor royalties:',
                      value: `-${priceString(total_amount_already_paid, currency)}`,
                    },
                    ...subItems,
                  ]
                : subItems
            }
            total={priceString(total_amount + vat_amount, currency)}
          />
        )
      }
    >
      {mainContent}
    </CheckoutLayout>
  )
}

export default DerivativePay
